
import Page from '@root/common/base/Page';
import ListSkeletonLoader from '@root/common/components/base/loader/ListSkeletonLoader.vue';
import { GetAuthorWithHeadlines } from '@root/modules/author/types/getAuthorWithHeadlines';
import { HeadlineSettings } from '@root/modules/headlines/types/headlines';
import AuthorWithHeadlinesService from '@root/modules/author/services/AuthorWithHeadlines.service';
import Author from '@root/modules/author/components/Author.vue';
import AuthorBreadcrumbs from '@root/modules/author/components/AuthorBreadcrumbs.vue';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import { BlockCollection } from '@headlines/components/block';
import { getEnv } from '@root/common/utils/getEnv';
import getAliasByLocale from '@root/common/utils/getAliasByLocale';

interface Data {
  author: GetAuthorWithHeadlines['data']['authors']['data'][0] | null;
  headlines: GetAuthorWithHeadlines['data']['authors']['data'][0]['articles'];
  headlineSettings: HeadlineSettings;
  titleBlock: Record<string, any>;
}

type JsonLd = {
  '@context': string;
  '@type': string;
  '@id': string;
  name: string;
  image?: Record<string, any>[];
  description?: string;
  memberOf?: Record<string, any>[];
};

export default Page.extend<Data, unknown, unknown, unknown>({
  components: {
    Author,
    AuthorBreadcrumbs,
    HeadlineList,
    ListSkeletonLoader,
    BlockType8: BlockCollection.BlockType8,
  },
  data() {
    return {
      author: null,
      headlines: {
        items: [],
        pager: {
          offset: 0,
          count: 0,
        },
      },
      headlineSettings: {
        author: false,
        publishDate: true,
        titleComment: false,
        type: 'grid',
      },
      titleBlock: {
        data: {
          title: {
            text: 'Autoriaus naujienos',
          },
        },
        settings: {
          text: true,
          colorBlock: '#3858f5',
        },
      },
    };
  },
  async fetch() {
    const limit = 20;
    const offset = getPageOffset(limit, Number(this.$route.query.page));
    const slug = this.$route.params.slug;

    if (slug) {
      try {
        const authorWithHeadlinesService = new AuthorWithHeadlinesService({ req: this.$ssrContext?.req });
        const data = await authorWithHeadlinesService.fetch({ slug, offset, limit });

        if (!data) {
          authorWithHeadlinesService.handleError({ message: 'author.error.not_found', statusCode: 404 });
          return;
        }

        this.author = data;

        if (data.articles.items && data.articles.items.length) {
          this.headlines.items = data.articles.items.filter(
            (item) => item.primaryCategory.channel.url !== 'www.delfi.lt/elta' && item.primaryCategory.channel.url !== 'www.delfi.lt/fone'
          );
        }

        if (data.articles.pager) {
          this.headlines.pager = data.articles.pager;
        }
      } catch (e) {
        this.handlePageError(e);
      }
    }
  },
  methods: {
    changePage() {
      this.$fetch();
    },
  },
  mounted() {
    this.restoreScroll();
  },
  head() {
    const meta = this.getPageMeta();

    if (this.author) {
      const { name } = this.author;
      const isEmployee = this.author.employeeId;
      const query = this.$nuxt.context.query;
      const pageTitle = query?.page ? `${name}, ${this.$t('words.page')} ${query.page}` : name;

      meta.setTitle({ title: pageTitle });

      const description = this.author?.attributes?.find((item) => item.key === 'description')?.value || null;
      if (description) {
        meta.description = meta.ogDescription = description;
      }

      const picture = this.author.image;
      let pictureObject = null;
      if (typeof picture !== 'undefined') {
        const picture = this.author.image;
        const focuspoint = picture?.attributes?.cropperData?.focuspoint;
        const focusPointString = focuspoint?.x && focuspoint?.y ? `&fx=${focuspoint?.x}&fy=${focuspoint?.y}` : ``;
        const url = getEnv('mediaApiCropper');
        const pictureUrl = `${url}/${picture?.id}.jpg?w=800&h=1200${focusPointString}`;
        meta.imageData = { src: pictureUrl, width: 800, height: 1200 };
        pictureObject = {
          '@type': 'ImageObject',
          url: `${pictureUrl}/${picture?.id}.jpg?w=800&h=1200${focusPointString}`,
          width: '800',
          height: '1200',
        };
      }

      // Inject ld+json into head
      const { locale } = this.$channelConfig('settings');
      const { base } = this.$channelConfig('meta');
      const path = getAliasByLocale(locale, 'authors');

      const jsonLd: JsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        '@id': `${base.baseUrl}/${path}/${this.author.slug}`,
        name: this.author.name,
      };

      if (pictureObject) {
        jsonLd.image = [pictureObject];
      }

      if (description) {
        jsonLd.description = description;
      }

      if (isEmployee) {
        jsonLd.memberOf = [
          {
            '@type': 'Organization',
            name: base.title,
            logo: {
              '@type': 'ImageObject',
              url: base.imageData.src,
            },
          },
        ];
      }

      meta.script = [{ hid: 'article-ld-json', type: 'application/ld+json', json: jsonLd }];
    }

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
      script: meta.script,
    };
  },
});
